import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const Waxing = () => {
  const navigate = useNavigate();
  const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const benefits = [
    {
      title: 'Smooth, Hair-Free Skin',
      description: 'Removes hair from the root for silky smooth skin that lasts 3-6 weeks.'
    },
    {
      title: 'Finer Hair Regrowth',
      description: 'Regular waxing causes hair to grow back finer, softer, and sparser over time.'
    },
    {
      title: 'Exfoliation Benefits',
      description: 'Removes dead skin cells along with hair, leaving skin smoother and more radiant.'
    },
    {
      title: 'Less Irritation Than Shaving',
      description: 'Eliminates razor burn, cuts, and nicks commonly associated with shaving.'
    },
    {
      title: 'Reduces Ingrown Hairs',
      description: 'Properly performed waxing significantly reduces the occurrence of ingrown hairs.'
    },
    {
      title: 'Long-Lasting Results',
      description: 'Enjoy smooth skin for weeks without daily maintenance or stubble.'
    },
    {
      title: 'Precision & Clean Lines',
      description: 'Creates clean, precise lines and shapes for brows, bikini areas, and facial hair.'
    },
    {
      title: 'Quick Treatment Time',
      description: 'Most waxing sessions take only 15-30 minutes depending on the area.'
    },
    {
      title: 'Suitable for Multiple Areas',
      description: 'Effectively removes unwanted hair from face, body, and intimate areas.'
    },
    {
      title: 'Convenient & Efficient',
      description: 'Eliminates daily shaving routine, saving time and effort long-term.'
    },
    {
      title: 'Professional Results',
      description: 'Experienced estheticians ensure thorough, safe hair removal with optimal results.'
    },
    {
      title: 'Cost-Effective',
      description: 'More economical than daily razors and shaving products over time.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Professional Waxing Services in Forest Hills, NY | Century Aesthetics</title>
        <meta name="description" content="Expert waxing services for smooth, hair-free skin. Brazilian, bikini, facial, and body waxing in Forest Hills, NY." />
        <meta name="keywords" content="waxing, brazilian wax, bikini wax, facial waxing, body waxing, hair removal" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/waxing" />
        
        <meta property="og:title" content="Professional Waxing Services | Century Longevity & Aesthetics" />
        <meta property="og:description" content="Expert waxing services for smooth, hair-free skin. Brazilian, bikini, facial, and body waxing in Forest Hills, NY." />
        <meta property="og:url" content="https://www.centuryaesthetics.org/waxing" />
      </Helmet>

      <div className="min-h-screen bg-white pt-28">
     
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="/wax.jpeg" 
                  alt="Professional Waxing Services" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6">
                PROFESSIONAL WAXING SERVICES FOR SMOOTH, LONG-LASTING RESULTS
              </h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Professional waxing offers an effective hair removal solution that delivers smooth, hair-free skin lasting 3-6 weeks. Unlike shaving, waxing removes hair from the root, resulting in finer, softer regrowth over time. Our experienced estheticians provide comfortable, efficient waxing services for face, body, and intimate areas using high-quality wax formulated to minimize discomfort while maximizing results. From eyebrow shaping to full body waxing, we offer comprehensive services tailored to your needs.
                </p>
                <p>
                  Waxing not only removes unwanted hair but also exfoliates dead skin cells, leaving your skin smoother and more radiant. Regular waxing reduces the frequency of hair removal needed as hair grows back finer and sparser. Our professional techniques minimize irritation, reduce ingrown hairs, and create clean, precise results. Schedule your waxing appointment at Century Longevity & Aesthetics for smooth, beautiful skin that lasts.
                </p>
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="mt-8 bg-olive-darkest text-white px-8 py-4 rounded font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-white mb-4">Waxing Pricing</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-5xl mx-auto bg-white rounded-lg p-8 shadow-2xl">
            
            {/* Pricing Tables */}
            <div className="overflow-x-auto">
              {/* Face Table */}
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-olive-darkest text-white">
                    <th className="px-4 py-3 text-left">FACE</th>
                    <th className="px-4 py-3 text-center">Price</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b"><td className="px-4 py-3">Nose</td><td className="px-4 py-3 text-center">$15</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Ears</td><td className="px-4 py-3 text-center">$15</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Eyebrows</td><td className="px-4 py-3 text-center">$21</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Sideburns</td><td className="px-4 py-3 text-center">$17</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Cheeks</td><td className="px-4 py-3 text-center">$17</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Upper Lip</td><td className="px-4 py-3 text-center">$15</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Chin</td><td className="px-4 py-3 text-center">$16</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Neck/Neckline</td><td className="px-4 py-3 text-center">$16</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Hair Line</td><td className="px-4 py-3 text-center">$15</td></tr>
                  <tr className="border-b"><td className="px-4 py-3 font-semibold">Full Face</td><td className="px-4 py-3 text-center font-semibold">$56</td></tr>
                </tbody>
              </table>

              {/* Upper Body Table */}
              <table className="w-full text-sm mt-6">
                <thead>
                  <tr className="bg-olive-darkest text-white">
                    <th className="px-4 py-3 text-left">UPPER BODY</th>
                    <th className="px-4 py-3 text-center">Price</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b"><td className="px-4 py-3">Underarms</td><td className="px-4 py-3 text-center">$25</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Areolas</td><td className="px-4 py-3 text-center">$15</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Belly Line</td><td className="px-4 py-3 text-center">$11</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Shoulders</td><td className="px-4 py-3 text-center">$25</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Half Arms</td><td className="px-4 py-3 text-center">$38</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Full Arms</td><td className="px-4 py-3 text-center">$40</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Full Abdomen</td><td className="px-4 py-3 text-center">$40</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Full Back</td><td className="px-4 py-3 text-center">$65</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Upper Back</td><td className="px-4 py-3 text-center">$30</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Mid Back</td><td className="px-4 py-3 text-center">$30</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Lower Back</td><td className="px-4 py-3 text-center">$25</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Chest Strip</td><td className="px-4 py-3 text-center">$18</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Full Chest</td><td className="px-4 py-3 text-center">$38</td></tr>
                </tbody>
              </table>

              {/* Lower Body Table */}
              <table className="w-full text-sm mt-6">
                <thead>
                  <tr className="bg-olive-darkest text-white">
                    <th className="px-4 py-3 text-left">LOWER BODY</th>
                    <th className="px-4 py-3 text-center">Price</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b"><td className="px-4 py-3">Lower Legs</td><td className="px-4 py-3 text-center">$40</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Upper Legs</td><td className="px-4 py-3 text-center">$45</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Full Legs</td><td className="px-4 py-3 text-center">$85</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Brazilian</td><td className="px-4 py-3 text-center">$65</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Manzilian</td><td className="px-4 py-3 text-center">$65</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Full Buttocks</td><td className="px-4 py-3 text-center">$18</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Buttocks Strip</td><td className="px-4 py-3 text-center">$10</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Full Bikini</td><td className="px-4 py-3 text-center">$55</td></tr>
                  <tr className="border-b"><td className="px-4 py-3">Bikini Strip</td><td className="px-4 py-3 text-center">$45</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Benefits of Professional Waxing</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* Carousel Container */}
            <div className="relative">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Previous benefit"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Next Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Next benefit"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Carousel Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentBenefitIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-lg shadow-xl p-8 sm:p-12 min-h-[300px] flex flex-col justify-center"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-16 h-16 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-3xl font-semibold text-olive-darkest mb-4">
                        {benefits[currentBenefitIndex].title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        {benefits[currentBenefitIndex].description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {benefits.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBenefitIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentBenefitIndex 
                        ? 'w-8 bg-gold' 
                        : 'w-2 bg-gray-300 hover:bg-gold/50'
                    }`}
                    aria-label={`Go to benefit ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 bg-olive-darkest text-white p-8 sm:p-10 md:p-12 rounded-xl shadow-2xl text-center"
          >
            <p className="text-base sm:text-lg leading-relaxed mb-8 text-white">
              Experience smooth, hair-free skin that lasts with our professional waxing services!
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="w-full sm:w-auto bg-gold text-olive-darkest px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation mb-10"
            >
              Schedule Appointment
            </motion.button>

            <div className="w-24 h-0.5 bg-gold/30 mx-auto mb-8"></div>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 text-white">Have Questions?</h3>
            <p className="text-base sm:text-lg text-white mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
              Learn about waxing services, what to expect during your appointment, and proper aftercare.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  navigate('/faq');
                  setTimeout(() => {
                    const element = document.getElementById('waxing');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                View FAQs
              </motion.button>
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-gold px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                Contact Us Directly
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Waxing;